import React from 'react'
import { useNavigate } from 'react-router-dom'

export default function Landing() {
  const navigate = useNavigate()

  const features = [
    { icon: '🎤', title: 'Journal your way', description: 'Type, speak, or use Walk and Talk. Voice journal while you walk. Velumio transcribes and analyses everything automatically.' },
    { icon: '◈', title: 'Track patterns passively', description: 'Your activity patterns, mood trends, and financial stress combine into a weekly wellbeing score that actually means something.' },
    { icon: '✉', title: 'Receive a personal letter', description: 'Every week, Claude writes you a personal letter based on your entries. Honest, warm, and written just for you.' },
    { icon: '⚡', title: 'Know your energy', description: 'Every morning, your Energy Budget tells you how much you have in the tank and what to protect it with today.' },
  ]

  const stats = [
    { stat: '7.5/10', label: 'Relevancy score from independent assessment' },
    { stat: '9.99', label: 'Per month. Less than a coffee a week.' },
    { stat: '2-layer', label: 'Crisis detection. Keyword and AI semantic.' },
    { stat: 'Weekly', label: 'Personal letter written by Claude AI just for you.' },
  ]

  const teamFeatures = [
    'Full anonymisation. No individual data ever visible.',
    'Weekly trend charts and risk distribution.',
    'Automated alerts when burnout risk rises.',
    'Minimum 5 team members before any data shows.',
  ]

  return (
    <div style={{ minHeight: '100vh', background: 'var(--cream)', fontFamily: 'var(--font-body)' }}>

      <style>{`
        .hero-video {
          position: absolute;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          object-fit: cover;
          object-position: center center;
          z-index: 0;
        }
        .nav-btn-secondary {
          padding: 7px 14px;
          font-size: 13px;
          background: transparent;
          border: 1.5px solid rgba(248,244,238,0.4);
          border-radius: 9999px;
          cursor: pointer;
          color: #F8F4EE;
          font-family: inherit;
          white-space: nowrap;
        }
        .nav-btn-primary {
          padding: 7px 14px;
          font-size: 13px;
          background: #C8963E;
          color: #0F1B2D;
          border: none;
          border-radius: 9999px;
          cursor: pointer;
          font-family: inherit;
          font-weight: 600;
          white-space: nowrap;
        }
        .hero-subtitle {
          font-size: clamp(14px, 2vw, 18px);
          color: rgba(248,244,238,0.85);
          line-height: 1.8;
          margin: 0 auto 16px auto;
          text-align: center;
          max-width: 520px;
          padding: 0 16px;
        }
        .hero-cta-row {
          display: flex;
          gap: 12px;
          flex-wrap: wrap;
          justify-content: center;
          padding: 0 16px;
        }
        .hero-btn-primary {
          padding: 14px 32px;
          font-size: 15px;
          font-weight: 600;
          color: #0F1B2D;
          background: #C8963E;
          border: none;
          border-radius: 9999px;
          cursor: pointer;
          font-family: inherit;
          box-shadow: 0 8px 32px rgba(200,150,62,0.4);
          white-space: nowrap;
        }
        .hero-btn-secondary {
          padding: 14px 32px;
          font-size: 15px;
          color: #F8F4EE;
          background: transparent;
          border: 1.5px solid rgba(248,244,238,0.5);
          border-radius: 9999px;
          cursor: pointer;
          font-family: inherit;
          white-space: nowrap;
        }
        @media (max-width: 480px) {
          .hero-btn-primary, .hero-btn-secondary {
            width: 100%;
            text-align: center;
          }
          .hero-cta-row {
            flex-direction: column;
            align-items: stretch;
          }
        }
      `}</style>

      {/* NAVBAR */}
      <div style={{
        position: 'fixed', top: 0, left: 0, right: 0, zIndex: 100,
        padding: '0 20px', height: '60px',
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        background: 'rgba(15,27,45,0.75)',
        backdropFilter: 'blur(12px)',
        borderBottom: '1px solid rgba(248,244,238,0.1)',
      }}>
        <div style={{ fontFamily: 'var(--font-display)', fontSize: '20px', fontWeight: 700, color: 'var(--cream)', flexShrink: 0 }}>
          Velu<span style={{ color: 'var(--gold)' }}>mio</span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexShrink: 0 }}>
          <button className="nav-btn-secondary" onClick={() => navigate('/login')}>
            Sign in
          </button>
          <button className="nav-btn-primary" onClick={() => navigate('/register')}>
            Get started
          </button>
        </div>
      </div>

      {/* HERO WITH VIDEO */}
      <div style={{
        position: 'relative',
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column' as const,
        alignItems: 'center',
        justifyContent: 'center',
        textAlign: 'center',
        padding: '100px 0 80px',
        overflow: 'hidden',
      }}>
        <video autoPlay muted loop playsInline className="hero-video">
          <source src="/hero-video.mp4" type="video/mp4" />
        </video>

        <div style={{
          position: 'absolute', top: 0, left: 0, right: 0, bottom: 0,
          background: 'linear-gradient(135deg, rgba(15,27,45,0.88) 0%, rgba(15,27,45,0.72) 100%)',
          zIndex: 1,
        }} />

        <div style={{ position: 'relative', zIndex: 2, width: '100%', maxWidth: '680px', margin: '0 auto', padding: '0 20px', boxSizing: 'border-box' as const }}>

          <div style={{
            display: 'inline-flex', alignItems: 'center', gap: '8px',
            padding: '5px 14px', background: 'rgba(200,150,62,0.2)',
            border: '1px solid rgba(200,150,62,0.5)', borderRadius: '9999px',
            fontSize: '11px', color: 'var(--gold)', fontWeight: 600,
            letterSpacing: '0.05em', marginBottom: '24px',
          }}>
            AI-Powered Burnout Detection
          </div>

          <h1 style={{
            fontFamily: 'var(--font-display)',
            fontSize: 'clamp(28px, 6vw, 64px)',
            fontWeight: 700, color: 'var(--cream)', lineHeight: 1.15,
            marginBottom: '20px',
            padding: '0 8px',
          }}>
            Know when you are heading
            <span style={{ color: 'var(--gold)', display: 'block' }}>
              for burnout before it hits.
            </span>
          </h1>

          <p className="hero-subtitle">
            Velumio combines voice journaling, AI analysis, and passive activity
            tracking to give you a weekly wellbeing score and a personal letter
            written just for you.
          </p>

          <p style={{
            fontSize: '14px', color: 'var(--gold)',
            fontStyle: 'italic', fontFamily: 'var(--font-display)',
            marginBottom: '36px', textAlign: 'center',
          }}>
            Now easy and comfortable to journal anywhere.
          </p>

          <div className="hero-cta-row">
            <button className="hero-btn-primary" onClick={() => navigate('/register')}>
              Start for free
            </button>
            <button className="hero-btn-secondary" onClick={() => navigate('/login')}>
              Sign in
            </button>
          </div>

          <p style={{ marginTop: '20px', fontSize: '12px', color: 'rgba(248,244,238,0.5)', textAlign: 'center' }}>
            Free to start. No credit card required.
          </p>
        </div>
      </div>

      {/* HOW IT WORKS */}
      <div style={{ padding: '80px 20px', background: 'var(--white)', textAlign: 'center' }}>
        <p style={{ fontSize: '11px', letterSpacing: '0.1em', textTransform: 'uppercase' as const, color: 'var(--gold)', fontWeight: 600, marginBottom: '12px' }}>
          How it works
        </p>
        <h2 style={{ fontFamily: 'var(--font-display)', fontSize: 'clamp(22px, 4vw, 40px)', color: 'var(--navy)', marginBottom: '12px' }}>
          Your wellbeing, understood deeply.
        </h2>
        <p style={{ fontSize: '15px', color: 'var(--muted)', maxWidth: '440px', margin: '0 auto 48px', lineHeight: 1.7 }}>
          Velumio works quietly in the background, learning your patterns and flagging changes before they become problems.
        </p>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '20px', maxWidth: '900px', margin: '0 auto' }}>
          {features.map((item, i) => (
            <div key={i} style={{ padding: '24px 20px', background: 'var(--cream)', borderRadius: 'var(--radius-lg)', border: '1px solid var(--cream-dark)', textAlign: 'left' }}>
              <div style={{ fontSize: '30px', marginBottom: '12px' }}>{item.icon}</div>
              <h3 style={{ fontFamily: 'var(--font-display)', fontSize: '16px', color: 'var(--navy)', marginBottom: '8px' }}>{item.title}</h3>
              <p style={{ fontSize: '13px', color: 'var(--muted)', lineHeight: 1.6 }}>{item.description}</p>
            </div>
          ))}
        </div>
      </div>

      {/* WHY VELUMIO */}
      <div style={{ padding: '80px 20px', background: 'var(--navy)', textAlign: 'center' }}>
        <p style={{ fontSize: '11px', letterSpacing: '0.1em', textTransform: 'uppercase' as const, color: 'var(--gold)', fontWeight: 600, marginBottom: '12px' }}>
          Why Velumio
        </p>
        <h2 style={{ fontFamily: 'var(--font-display)', fontSize: 'clamp(22px, 4vw, 40px)', color: 'var(--cream)', marginBottom: '12px' }}>
          Not another wellness app.
        </h2>
        <p style={{ fontSize: '15px', color: 'rgba(248,244,238,0.6)', maxWidth: '440px', margin: '0 auto 48px', lineHeight: 1.7 }}>
          Velumio is the only platform that combines passive activity tracking with emotional journaling to detect burnout before you feel it.
        </p>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: '16px', maxWidth: '900px', margin: '0 auto' }}>
          {stats.map((item, i) => (
            <div key={i} style={{ padding: '24px 16px', background: 'rgba(248,244,238,0.05)', borderRadius: 'var(--radius-lg)', border: '1px solid rgba(248,244,238,0.1)' }}>
              <p style={{ fontFamily: 'var(--font-display)', fontSize: 'clamp(24px, 4vw, 32px)', fontWeight: 700, color: 'var(--gold)', marginBottom: '8px' }}>{item.stat}</p>
              <p style={{ fontSize: '12px', color: 'rgba(248,244,238,0.6)', lineHeight: 1.5 }}>{item.label}</p>
            </div>
          ))}
        </div>
      </div>

      {/* FOR TEAMS */}
      <div style={{ padding: '80px 20px', background: 'var(--cream)', textAlign: 'center' }}>
        <p style={{ fontSize: '11px', letterSpacing: '0.1em', textTransform: 'uppercase' as const, color: 'var(--gold)', fontWeight: 600, marginBottom: '12px' }}>
          For organisations
        </p>
        <h2 style={{ fontFamily: 'var(--font-display)', fontSize: 'clamp(22px, 4vw, 40px)', color: 'var(--navy)', marginBottom: '12px' }}>
          See team wellbeing without seeing individuals.
        </h2>
        <p style={{ fontSize: '15px', color: 'var(--muted)', maxWidth: '520px', margin: '0 auto 40px', lineHeight: 1.7 }}>
          HR teams get an anonymised aggregate dashboard showing burnout risk trends, team alerts, and score distribution with zero access to individual data, names, or journal content.
        </p>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '16px', maxWidth: '700px', margin: '0 auto 36px' }}>
          {teamFeatures.map((item, i) => (
            <div key={i} style={{ padding: '18px', background: 'var(--white)', borderRadius: 'var(--radius-md)', border: '1px solid var(--cream-dark)', fontSize: '13px', color: 'var(--ink-soft)', lineHeight: 1.6, textAlign: 'left' }}>
              {item}
            </div>
          ))}
        </div>
        <button
          onClick={() => navigate('/register')}
          style={{ padding: '14px 32px', fontSize: '15px', fontWeight: 600, color: 'var(--white)', background: 'var(--gold)', border: 'none', borderRadius: '9999px', cursor: 'pointer', fontFamily: 'var(--font-body)' }}
        >
          Get started for your team
        </button>
      </div>

      {/* CTA */}
      <div style={{ padding: '80px 20px', background: 'linear-gradient(135deg, var(--navy) 0%, #1A3A5C 100%)', textAlign: 'center' }}>
        <h2 style={{ fontFamily: 'var(--font-display)', fontSize: 'clamp(22px, 4vw, 44px)', color: 'var(--cream)', marginBottom: '16px', lineHeight: 1.2 }}>
          Start understanding yourself better.
        </h2>
        <p style={{ fontSize: '15px', color: 'rgba(248,244,238,0.6)', maxWidth: '360px', margin: '0 auto 32px', lineHeight: 1.7 }}>
          Free to start. No credit card. Just honest, private, AI-powered wellbeing support.
        </p>
        <button
          onClick={() => navigate('/register')}
          style={{ padding: '16px 44px', fontSize: '16px', fontWeight: 600, color: 'var(--navy)', background: 'var(--gold)', border: 'none', borderRadius: '9999px', cursor: 'pointer', fontFamily: 'var(--font-body)' }}
        >
          Create your free account
        </button>
      </div>

      {/* FOOTER */}
      <div style={{ padding: '28px 20px', background: 'var(--navy)', borderTop: '1px solid rgba(248,244,238,0.1)', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap' as const, gap: '12px' }}>
        <div style={{ fontFamily: 'var(--font-display)', fontSize: '16px', fontWeight: 700, color: 'var(--cream)' }}>
          Velu<span style={{ color: 'var(--gold)' }}>mio</span>
        </div>
        <div style={{ fontSize: '11px', color: 'rgba(248,244,238,0.3)' }}>
          2026 Olatunji Ventures Ltd.
        </div>
        <div style={{ display: 'flex', gap: '20px' }}>
          <span onClick={() => navigate('/privacy')} style={{ fontSize: '11px', color: 'rgba(248,244,238,0.4)', cursor: 'pointer' }}>Privacy</span>
          <span onClick={() => navigate('/terms')} style={{ fontSize: '11px', color: 'rgba(248,244,238,0.4)', cursor: 'pointer' }}>Terms</span>
          <a href="mailto:hello@velumio.co.uk" style={{ fontSize: '11px', color: 'rgba(248,244,238,0.4)', textDecoration: 'none' }}>Contact</a>
        </div>
      </div>

    </div>
  )
}